import React, { useEffect, useRef, useState } from 'react';
import { useThemeMode } from '../../design-system/theme';
import '../../pages/Checkout/CheckoutTheme.css';
import { Spinner } from 'react-bootstrap';
import './HostedCheckout.css';
import { capturePostHogEvent, trackBeginCheckout } from '../../utils/analytics';
import { createCheckoutAttemptId } from '../../utils/checkoutAttemptId';
import {
  createHostedCheckoutSession,
  HOSTED_CHECKOUT_FEATURE_FLAG,
  HostedCheckoutError,
  type HostedCheckoutItem,
} from '../../utils/hostedCheckout';

type Props = {
  items: HostedCheckoutItem[];
  promoCode?: string | null;
  returnContext: 'subscriptions' | 'signup_survey' | 'ai_agent';
  sourceRoute: string;
  flowContext?: string | null;
  surface: string;
  assignment: {
    assignmentId: string;
    subjectId: string;
    exposedAt: string;
  };
  getApiToken?: (() => Promise<string | null>) | null;
};

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8082';

/**
 * Relaunch guard. Stripe showed the same buyers opening three or four payment
 * pages within a minute (Sep 3 to 9 2026): this component auto-created a new
 * session on every mount, so the browser Back button from Stripe sent them
 * straight to a fresh payment page. The launch is remembered per tab and per
 * purchase; a remembered purchase waits for one click instead.
 */
const LAUNCH_MARKER_KEY = 'botspot.hostedCheckout.launched';

function readLaunchMarker(): string | null {
  try {
    return window.sessionStorage.getItem(LAUNCH_MARKER_KEY);
  } catch {
    return null;
  }
}

function writeLaunchMarker(launchKey: string) {
  try {
    window.sessionStorage.setItem(LAUNCH_MARKER_KEY, launchKey);
  } catch {
    // Private mode or blocked storage: the guard degrades to today's behavior.
  }
}

export default function HostedCheckoutLauncher({
  items,
  promoCode,
  returnContext,
  sourceRoute,
  flowContext,
  surface,
  assignment,
  getApiToken,
}: Props) {
  const [checkoutTheme] = useThemeMode();
  const attemptIdRef = useRef<string | null>(null);
  if (!attemptIdRef.current) attemptIdRef.current = createCheckoutAttemptId('hosted_checkout');
  const requestRef = useRef<{
    attemptId: string;
    purchaseKey: string;
    promise: ReturnType<typeof createHostedCheckoutSession>;
  } | null>(null);
  const [attemptNumber, setAttemptNumber] = useState(0);
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState<string>();
  const [omitPromoCode, setOmitPromoCode] = useState(false);
  const purchaseKey = JSON.stringify({
    items,
    promoCode: omitPromoCode ? null : promoCode,
    returnContext,
    sourceRoute,
    flowContext,
    surface,
    assignment,
  });
  // What the tab remembers: the purchase itself, not the assignment or route,
  // so a returning buyer is recognised even if the experiment re-exposes.
  const launchKey = JSON.stringify({
    items,
    promoCode: omitPromoCode ? null : promoCode,
    returnContext,
    surface,
  });
  const [awaitingResume, setAwaitingResume] = useState(() => readLaunchMarker() === launchKey);
  const [resumePending, setResumePending] = useState(false);

  useEffect(() => {
    let active = true;
    if (awaitingResume) return undefined;
    if (requestRef.current && requestRef.current.purchaseKey !== purchaseKey) {
      attemptIdRef.current = createCheckoutAttemptId('hosted_checkout');
      setError('');
      setErrorCode(undefined);
    }
    const checkoutAttemptId = attemptIdRef.current;
    if (!checkoutAttemptId) return;
    const eventProps = {
      checkout_attempt_id: checkoutAttemptId,
      checkout_experience_assignment_id: assignment.assignmentId,
      checkout_experience_subject_id: assignment.subjectId,
      checkout_experience_surface: surface,
      checkout_experience_variant: 'stripe_hosted',
      checkout_experience_feature_flag: HOSTED_CHECKOUT_FEATURE_FLAG,
      source: sourceRoute,
    };
    if (requestRef.current?.attemptId !== checkoutAttemptId) {
      capturePostHogEvent('checkout_experience_session_requested', eventProps);
      requestRef.current = {
        attemptId: checkoutAttemptId,
        purchaseKey,
        promise: (async () => {
          const token = getApiToken ? await getApiToken() : null;
          return createHostedCheckoutSession({
            apiUrl: API_URL,
            token,
            checkoutAttemptId,
            items,
            promoCode: omitPromoCode ? null : promoCode,
            returnContext,
            attribution: {
              sourceRoute,
              flowContext,
              experimentKey: HOSTED_CHECKOUT_FEATURE_FLAG,
              variant: 'stripe_hosted',
              assignmentId: assignment.assignmentId,
              subjectId: assignment.subjectId,
              surface,
              exposedAt: assignment.exposedAt,
            },
          });
        })(),
      };
    }
    void requestRef.current.promise
      .then((result) => {
        if (!active) return;
        capturePostHogEvent('checkout_experience_session_created', {
          ...eventProps,
          stripe_checkout_session_id: result.sessionId,
        });
        capturePostHogEvent('checkout_experience_redirected', eventProps);
        // Begin checkout for the Stripe-hosted arm. The buyer reaches checkout
        // when we hand them Stripe's payment page, so this is the arrival that
        // matches the control arm's on-page arrival. Keyed on the purchase and
        // the arm, so the relaunch guard, a re-render, or a Back-button return
        // never reports a second "reached checkout".
        trackBeginCheckout({
          dedupeKey: `hosted_checkout:${surface}:${launchKey}`,
          transactionId: checkoutAttemptId,
          checkout_surface: surface,
          plan_type: items.map((item) => item.productSlug).join(','),
          plan_key: items.map((item) => item.planKey).join(','),
          cart_item_count: items.length,
          flow_context: flowContext || null,
          stripe_checkout_session_id: result.sessionId,
          ...eventProps,
        });
        writeLaunchMarker(launchKey);
        window.location.assign(result.checkoutUrl);
      })
      .catch((launchError) => {
        if (!active) return;
        const message =
          launchError instanceof Error
            ? launchError.message
            : 'Secure checkout could not be opened. Please try again.';
        setError(message);
        setResumePending(false);
        setErrorCode(launchError instanceof HostedCheckoutError ? launchError.code : undefined);
        capturePostHogEvent('checkout_experience_launch_failed', {
          ...eventProps,
          error_message: message,
        });
      });
    return () => {
      active = false;
    };
  }, [
    assignment,
    attemptNumber,
    awaitingResume,
    launchKey,
    flowContext,
    getApiToken,
    items,
    omitPromoCode,
    promoCode,
    purchaseKey,
    returnContext,
    sourceRoute,
    surface,
  ]);

  const resume = () => {
    if (resumePending) return;
    setResumePending(true);
    attemptIdRef.current = createCheckoutAttemptId('hosted_checkout');
    setAwaitingResume(false);
  };

  const retry = (removePromo = false) => {
    attemptIdRef.current = createCheckoutAttemptId('hosted_checkout');
    if (removePromo) setOmitPromoCode(true);
    setError('');
    setErrorCode(undefined);
    setAttemptNumber((value) => value + 1);
  };

  return (
    <main className="checkout-page bsui-scope" data-bs-theme={checkoutTheme} aria-live="polite">
      <div className="checkout-loading hosted-checkout-status">
        {error ? (
          <>
            <h1>Secure checkout could not open</h1>
            <p role="alert">{error}</p>
            <button type="button" className="btn btn-primary" onClick={() => retry()}>
              Try again
            </button>
            {errorCode === 'INVALID_PROMOTION' && promoCode && !omitPromoCode && (
              <button type="button" className="btn btn-secondary" onClick={() => retry(true)}>
                Continue without promo code
              </button>
            )}
          </>
        ) : awaitingResume || resumePending ? (
          <>
            <h1>Your order is ready</h1>
            <p>You previously opened Stripe&apos;s secure checkout. Continue when you are ready.</p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={resume}
              disabled={resumePending}
              aria-busy={resumePending}
            >
              Continue To Secure Checkout
            </button>
          </>
        ) : (
          <>
            <Spinner animation="border" />
            <span>Opening secure Stripe checkout…</span>
          </>
        )}
      </div>
    </main>
  );
}
